import { withJsonFormsControlProps } from "@jsonforms/react";
import { TextField, Tooltip } from "@mui/material";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTierRules } from "../../../../hooks/useTierRules";
import { useTiers } from "../../../../hooks/useTiers";
import Table from "../../../common/table/Table";
import BoosterTable from "../shared/BoosterTable";
import "../shared/pointsOutcome.scss";

const isMissingValue = (value) =>
  value === "" || value === null || value === undefined;

/**
 * AwardFlatPoints — outcome component for "award-flat-points"
 *
 * Stores in rule_data.points_calculation:
 * {
 *   rule_type: "flat_points",
 *   flat_points: {
 *     default_value: number,
 *     tier_values: { [tier_id]: number }
 *   },
 *   booster_multipliers: [...],
 *   booster_flat_points: [...]
 * }
 */
const AwardFlatPoints = (props) => {
  const { handleChange, path, data } = props;

  // Initialize state from existing data
  // data is already scoped to points_calculation by JsonForms
  // Handle legacy double-nested structure: data may be { points_calculation: { ... } }
  const pointsCalc = data?.points_calculation || data || {};
  const flatPoints = pointsCalc.flat_points || {};

  const [mode, setMode] = useState(
    flatPoints.tier_values && Object.keys(flatPoints.tier_values).length > 0
      ? "tier_based"
      : "fixed"
  );
  const [fixedValue, setFixedValue] = useState(flatPoints.default_value ?? "");
  const [tierValues, setTierValues] = useState(flatPoints.tier_values || {});
  const [boosterMultipliers, setBoosterMultipliers] = useState(
    pointsCalc.booster_multipliers || []
  );
  const [boosterFlatPoints, setBoosterFlatPoints] = useState(
    pointsCalc.booster_flat_points || []
  );
  const [hasBoosterValidationErrors, setHasBoosterValidationErrors] =
    useState(false);

  const { tierRules } = useTierRules();
  const { tiers, loading: tiersLoading } = useTiers();

  const isTierActive = tierRules?.status === "ACTIVE";
  const hasBasicConfigurationErrors =
    mode === "fixed"
      ? isMissingValue(fixedValue)
      : tiers.length === 0 ||
        tiers.some((tier) => isMissingValue(tierValues[tier.id]));
  const hasValidationErrors =
    hasBasicConfigurationErrors || hasBoosterValidationErrors;

  // Sync state to parent form data
  const syncData = useCallback(() => {
    const updated = {
      rule_type: "flat_points",
      flat_points: {
        default_value: Number(fixedValue) || 0,
        tier_values: mode === "tier_based" ? tierValues : {},
      },
      tier_multipliers_enabled: mode === "tier_based",
      product_multipliers: [],
      booster_multipliers: boosterMultipliers,
      booster_flat_points: boosterFlatPoints,
      _hasValidationErrors: hasValidationErrors,
    };
    handleChange(path, updated);
  }, [
    handleChange,
    path,
    fixedValue,
    mode,
    tierValues,
    boosterMultipliers,
    boosterFlatPoints,
    hasValidationErrors,
  ]);

  // Sync validation state before first paint so the parent button is disabled immediately
  useLayoutEffect(() => {
    syncData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    syncData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fixedValue,
    mode,
    tierValues,
    boosterMultipliers,
    boosterFlatPoints,
    hasValidationErrors,
  ]);

  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
  }, []);

  const handleTierValueChange = useCallback((tierId, value) => {
    if (value !== "" && Number(value) < 0) return;
    setTierValues((prev) => ({
      ...prev,
      [tierId]: value === "" ? "" : Number(value) || 0,
    }));
  }, []);

  // Tier table
  const tierHeaderData = useMemo(
    () => [
      { columnKey: "name", header: "TIER NAME" },
      {
        columnKey: "points",
        header: "POINTS *",
        render: (row) => (
          <TextField
            size="small"
            type="number"
            placeholder="Enter points"
            value={tierValues[row.id] ?? ""}
            required
            error={isMissingValue(tierValues[row.id])}
            onChange={(e) => handleTierValueChange(row.id, e.target.value)}
            inputProps={{ min: 0 }}
            style={{ width: 150 }}
          />
        ),
      },
    ],
    [tierValues, handleTierValueChange]
  );

  const tierRowData = useMemo(
    () =>
      tiers.map((t) => ({
        id: t.id,
        name: t.name,
        points: tierValues[t.id] ?? "",
      })),
    [tiers, tierValues]
  );

  return (
    <div className="points-outcome">
      {/* Basic Configuration */}
      <div className="section-card basic-config">
        <h3 className="section-title">Basic Configuration</h3>

        <div className="mode-cards">
          <div
            className={`mode-card ${mode === "fixed" ? "selected" : ""}`}
            onClick={() => handleModeChange("fixed")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleModeChange("fixed");
            }}
          >
            <div className="mode-card-title">Fixed</div>
            <div className="mode-card-description">
              Award the same number of points to all users in the campaign,
              regardless of their tier.
            </div>
          </div>
          <Tooltip
            title={
              !isTierActive
                ? "Tier multipliers are unavailable. No active tier structure is configured for this program. Create and activate a tier structure to enable tier multipliers."
                : ""
            }
            arrow
            placement="top"
          >
            <span></span>
            <div
              className={`mode-card ${mode === "tier_based" ? "selected" : ""} ${!isTierActive ? "disabled" : ""}`}
              onClick={() => {
                if (isTierActive) handleModeChange("tier_based");
              }}
              title={
                !isTierActive
                  ? "Tier multipliers are unavailable. No active tier structure is configured for this program. Create and activate a tier structure to enable tier multipliers."
                  : ""
              }
              role="button"
              tabIndex={isTierActive ? 0 : -1}
              onKeyDown={(e) => {
                if (isTierActive && (e.key === "Enter" || e.key === " "))
                  handleModeChange("tier_based");
              }}
            >
              <div className="mode-card-title">Per Tier</div>
              <div className="mode-card-description">
                Configure custom point values to be distributed based on specific
                user tiers.
              </div>
            </div>
          </Tooltip>
        </div>

        {mode === "fixed" && (
          <div className="fixed-points-input">
            <div className="fixed-points-label">
              <span>Fixed Points</span>
              <span className="required-label">*Required</span>
            </div>
            <TextField
              size="small"
              type="number"
              fullWidth
              placeholder="Enter points value"
              value={fixedValue}
              required
              error={isMissingValue(fixedValue)}
              helperText={isMissingValue(fixedValue) ? "Points are required" : ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || Number(val) >= 0) {
                  setFixedValue(val);
                }
              }}
              inputProps={{ min: 0 }}
            />
          </div>
        )}

        {mode === "tier_based" && (
          <>
            {!isTierActive ? (
              <div className="tier-inactive-message">
                <strong>Tier multipliers are unavailable.</strong>
                <div>
                  No active tier structure is configured for this program. Create and
                  activate a tier structure to enable tier multipliers.
                </div>
              </div>
            ) : tiersLoading ? (
              <p className="section-subtitle">Loading tiers...</p>
            ) : (
              <div className="tier-points-table">
                <Table
                  headerData={tierHeaderData}
                  rowData={tierRowData}
                  noDataLabel="No tiers available"
                />
              </div>
            )}
            {hasBasicConfigurationErrors && !tiersLoading && (
              <div className="validation-error">
                <span>&#9888;</span> Points are required for every tier
              </div>
            )}
          </>
        )}
      </div>

      {/* Time Based Booster */}
      <BoosterTable
        title="Time-Bound Booster"
        description="Configure campaign-specific boosters that temporarily increase points or add bonus flat points."
        boosterMultipliers={boosterMultipliers}
        boosterFlatPoints={boosterFlatPoints}
        onChangeMultipliers={setBoosterMultipliers}
        onChangeFlatPoints={setBoosterFlatPoints}
        onValidationChange={setHasBoosterValidationErrors}
      />
    </div>
  );
};

export const AwardFlatPointsControl = withJsonFormsControlProps(AwardFlatPoints);

export default AwardFlatPoints;
